
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../../../components/feature/LanguageToggle';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const navigateToHome = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20 lg:h-24">
          {/* Logo */}
          <a 
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigateToHome();
            }}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div className="h-10 sm:h-12 lg:h-14 flex items-center justify-center">
              <img 
                src="https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/1d2b59dfb9c0bc727c9fa79ffa1643de.png"
                alt="RanchFire Radio Logo"
                className="h-full w-auto"
              />
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className={`hidden lg:flex items-center gap-6 xl:gap-8 px-6 py-2 rounded-full transition-all duration-300 ${
            isScrolled 
              ? 'bg-amber-50 border border-amber-200 shadow-sm' 
              : 'bg-white/10 backdrop-blur-sm border border-white/20'
          }`}>
            <a 
              href="/" 
              onClick={(e) => {
                e.preventDefault();
                navigateToHome();
              }}
              className={`font-bold text-sm uppercase tracking-wide transition-colors hover:text-amber-700 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-amber-100'
              }`}
            >
              {t('nav.home')}
            </a>
            <a 
              href="#listen" 
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('listen');
              }}
              className={`font-bold text-sm uppercase tracking-wide transition-colors hover:text-amber-700 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-amber-100'
              }`}
            >
              {t('nav.listen')}
            </a>
            <a 
              href="#about" 
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('about');
              }}
              className={`font-bold text-sm uppercase tracking-wide transition-colors hover:text-amber-700 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-amber-100'
              }`}
            >
              {t('nav.about')}
            </a>
            <a 
              href="/artists" 
              className={`font-bold text-sm uppercase tracking-wide transition-colors hover:text-amber-700 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-amber-100'
              }`}
            >
              {t('nav.forArtists')}
            </a>
            <a 
              href="/partners" 
              className={`font-bold text-sm uppercase tracking-wide transition-colors hover:text-amber-700 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-amber-100'
              }`}
            >
              {t('nav.brandsPartners')}
            </a>
            <a 
              href="/events" 
              className={`font-bold text-sm uppercase tracking-wide transition-colors hover:text-amber-700 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-amber-100'
              }`}
            >
              {t('nav.ranchfireLive')}
            </a>
            <a 
              href="/media" 
              className={`font-bold text-sm uppercase tracking-wide transition-colors hover:text-amber-700 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-amber-100'
              }`}
            >
              {t('nav.media')}
            </a>
            <a 
              href="/blog" 
              className={`font-bold text-sm uppercase tracking-wide transition-colors hover:text-amber-700 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-amber-100'
              }`}
            >
              {t('nav.podcast')}
            </a>
            <a 
              href="#contact" 
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('contact');
              }}
              className={`font-bold text-sm uppercase tracking-wide transition-colors hover:text-amber-700 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-amber-100'
              }`}
            >
              {t('nav.contact')}
            </a>
          </div>

          {/* Social Media Icons & CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Toggle */}
            <LanguageToggle isScrolled={isScrolled} />
            
            {/* Social Icons */}
            <div className="flex items-center gap-2">
              <a 
                href="https://www.facebook.com/profile.php?id=61580107936003" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer ${
                  isScrolled 
                    ? 'text-gray-900 hover:bg-amber-100 hover:text-amber-700' 
                    : 'text-amber-100 hover:bg-white/20'
                }`}
              >
                <i className="ri-facebook-fill text-base"></i>
              </a>
              <a 
                href="#" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer ${
                  isScrolled 
                    ? 'text-gray-900 hover:bg-amber-100 hover:text-amber-700' 
                    : 'text-amber-100 hover:bg-white/20'
                }`}
              >
                <i className="ri-twitter-x-line text-base"></i>
              </a>
              <a 
                href="https://www.instagram.com/ranchfire_radio/" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer ${
                  isScrolled 
                    ? 'text-gray-900 hover:bg-amber-100 hover:text-amber-700' 
                    : 'text-amber-100 hover:bg-white/20'
                }`}
              >
                <i className="ri-instagram-line text-base"></i>
              </a>
              <a 
                href="#" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer ${
                  isScrolled 
                    ? 'text-gray-900 hover:bg-amber-100 hover:text-amber-700' 
                    : 'text-amber-100 hover:bg-white/20'
                }`}
              >
                <i className="ri-spotify-fill text-base"></i>
              </a>
              <a 
                href="#" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer ${
                  isScrolled 
                    ? 'text-gray-900 hover:bg-amber-100 hover:text-amber-700' 
                    : 'text-amber-100 hover:bg-white/20'
                }`}
              >
                <i className="ri-youtube-fill text-base"></i>
              </a>
            </div>

            {/* CTA Button */}
            <a
              href="#listen"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('listen');
              }}
              className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-full font-bold uppercase text-sm tracking-wide transition-colors shadow-lg cursor-pointer whitespace-nowrap"
            >
              {t('nav.listenNow')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-gray-900' : 'text-amber-100'
            }`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-gray-900 border-t border-gray-800 shadow-xl">
            <div className="flex flex-col py-4">
              <a 
                href="/" 
                onClick={(e) => {
                  e.preventDefault();
                  navigateToHome();
                }}
                className="px-6 py-3 font-bold text-sm uppercase tracking-wide transition-colors hover:bg-gray-800 hover:text-amber-600 cursor-pointer text-amber-100"
              >
                {t('nav.home')}
              </a>
              <a 
                href="#listen" 
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('listen');
                }}
                className="px-6 py-3 font-bold text-sm uppercase tracking-wide transition-colors hover:bg-gray-800 hover:text-amber-600 cursor-pointer text-amber-100"
              >
                {t('nav.listen')}
              </a>
              <a 
                href="#about" 
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('about');
                }}
                className="px-6 py-3 font-bold text-sm uppercase tracking-wide transition-colors hover:bg-gray-800 hover:text-amber-600 cursor-pointer text-amber-100"
              >
                {t('nav.about')}
              </a>
              <a href="/artists" className="px-6 py-3 font-bold text-sm uppercase tracking-wide transition-colors hover:bg-gray-800 hover:text-amber-600 cursor-pointer text-amber-100">
                {t('nav.forArtists')}
              </a>
              <a href="/partners" className="px-6 py-3 font-bold text-sm uppercase tracking-wide transition-colors hover:bg-gray-800 hover:text-amber-600 cursor-pointer text-amber-100">
                {t('nav.brandsPartners')}
              </a>
              <a href="/events" className="px-6 py-3 font-bold text-sm uppercase tracking-wide transition-colors hover:bg-gray-800 hover:text-amber-600 cursor-pointer text-amber-100">
                {t('nav.ranchfireLive')}
              </a>
              <a href="/media" className="px-6 py-3 font-bold text-sm uppercase tracking-wide transition-colors hover:bg-gray-800 hover:text-amber-600 cursor-pointer text-amber-100">
                {t('nav.media')}
              </a>
              <a 
                href="/blog" 
                className="px-6 py-3 font-bold text-sm uppercase tracking-wide transition-colors hover:bg-gray-800 hover:text-amber-600 cursor-pointer text-amber-100"
              >
                {t('nav.podcast')}
              </a>
              <a 
                href="#contact" 
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('contact');
                }}
                className="px-6 py-3 font-bold text-sm uppercase tracking-wide transition-colors hover:bg-gray-800 hover:text-amber-600 cursor-pointer text-amber-100"
              >
                {t('nav.contact')}
              </a>
              
              {/* Mobile Language Toggle */}
              <div className="px-6 py-4 border-t border-gray-800 mt-2">
                <p className="text-amber-100 text-xs uppercase tracking-wide font-bold mb-3">Language</p>
                <LanguageToggle isMobile />
              </div>
              
              {/* Mobile Social Icons */}
              <div className="px-6 py-4 border-t border-gray-800">
                <p className="text-amber-100 text-xs uppercase tracking-wide font-bold mb-3">{t('nav.followUs')}</p>
                <div className="flex gap-3">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61580107936003" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-700 hover:bg-amber-600 text-white transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-lg"></i>
                  </a>
                  <a 
                    href="#" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-700 hover:bg-amber-600 text-white transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-twitter-x-line text-lg"></i>
                  </a>
                  <a 
                    href="https://www.instagram.com/ranchfire_radio/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-700 hover:bg-amber-600 text-white transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-instagram-line text-lg"></i>
                  </a>
                  <a 
                    href="#" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-700 hover:bg-amber-600 text-white transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-spotify-fill text-lg"></i>
                  </a>
                  <a 
                    href="#" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-700 hover:bg-amber-600 text-white transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-youtube-fill text-lg"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
